"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var Declarative_1 = require("../../utils/Declarative");
var FocusIndicator_1 = require("../../utils/FocusIndicator");
var Math_1 = require("../../utils/Math");
var TabActivatorFactory_1 = require("./TabActivatorFactory");
var TabPanel_1 = require("./TabPanel");
var Styles = require("./TabsWidgetStyles");
var TabWidget_1 = require("./TabWidget");
/**
 * Tabs widget component that renders a tab list and tab panel(s). It renders the active tab's content, in addition to the
 * content of the anchor tab (user-specified), which is rendered to maintain a consistent height when switching tabs.
 *
 * Nomenclature (inspired by https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel):
 *   - tabs widget: the name of the entire widget
 *   - tab list: a set of tab widgets
 *   - tab: an item in the tab list that serves as a label for one of the tab panels; when active, its panel is shown
 *   - tab indicator: the solid line at the bottom of each tab that changes color if the tab is active, hovered, or pressed
 *   - tab panel: the component that contains the content associated with a tab
 */
/**
 * Mode that dictates whether tabs are activated automatically or manually.
 *   Automatic: tabs are automatically activated and their panel is displayed when they receive focus
 *   Manual: users activate a tab and display its panel by pressing space or enter
 * More information: https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
 */
var TabActivationMode;
(function (TabActivationMode) {
    TabActivationMode[TabActivationMode["Automatic"] = 0] = "Automatic";
    TabActivationMode[TabActivationMode["Manual"] = 1] = "Manual";
})(TabActivationMode = exports.TabActivationMode || (exports.TabActivationMode = {}));
/**
 * Dictates the alignment of the tabs within the tab container.
 *   Justified: evenly distributes the tabs within the available container width
 *   Left: left aligns the tabs within the container
 */
var TabListAlignment;
(function (TabListAlignment) {
    TabListAlignment[TabListAlignment["Left"] = 0] = "Left";
    TabListAlignment[TabListAlignment["Justified"] = 1] = "Justified";
})(TabListAlignment = exports.TabListAlignment || (exports.TabListAlignment = {}));
var TabsWidget = /** @class */ (function (_super) {
    tslib_1.__extends(TabsWidget, _super);
    function TabsWidget(props) {
        var _this = _super.call(this, props) || this;
        _this.setActiveIndex = function (index) {
            // Any active index should also gain focus
            _this.setState({ activeIndex: _this.state.activeIndex === index ? _this.state.activeIndex : index, focusedIndex: index });
        };
        _this.setFocusedIndex = function (index) {
            _this.setState({ focusedIndex: index });
        };
        _this.state = { activeIndex: Math_1.clamp(props.initialActiveIndex, 0, props.tabs.length - 1), focusedIndex: -1 };
        _this.activator = TabActivatorFactory_1.TabActivatorFactory.CreateInstance(_this);
        return _this;
    }
    TabsWidget.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (this.state.activeIndex !== prevState.activeIndex && this.props.onActiveIndexChanged) {
            this.props.onActiveIndexChanged(this.state.activeIndex);
        }
    };
    TabsWidget.prototype.buildTabId = function (index) {
        return Declarative_1.makeTestId(this.props.testId, "tab" + (index + 1));
    };
    TabsWidget.prototype.buildTabPanelId = function (index) {
        return Declarative_1.makeTestId(this.props.testId, "tabpanel" + (index + 1));
    };
    TabsWidget.prototype.renderTabList = function () {
        var _this = this;
        return this.props.tabs.map(function (tab, index) {
            var tabProps = {
                ariaControls: _this.buildTabPanelId(index),
                id: _this.buildTabId(index),
                index: index,
                isActive: _this.isTabActive(index),
                isFocused: _this.isTabFocused(index),
                onBlur: function () { return _this.setFocusedIndex(-1); },
                onClick: _this.setActiveIndex,
                onFocus: _this.setFocusedIndex,
                onKeyDown: _this.activator.handleKeyDown,
                tabContainerStyles: Styles.GetTabLabelContainerStyles(),
                tabContent: tab.tabContent
            };
            if (_this.props.dimensionOverrides && _this.props.dimensionOverrides.tabHorizontalPadding) {
                tabProps.tabContainerStyles.paddingRight = _this.props.dimensionOverrides.tabHorizontalPadding;
                tabProps.tabContainerStyles.paddingLeft = _this.props.dimensionOverrides.tabHorizontalPadding;
            }
            if (_this.props.tabListAlignment === undefined || _this.props.tabListAlignment === TabListAlignment.Justified) {
                tabProps.flexPercent = 100 / _this.props.tabs.length;
            }
            return React.createElement(TabWidget_1.TabWidget, tslib_1.__assign({ key: index }, tabProps));
        });
    };
    TabsWidget.prototype.isTabActive = function (tabIndex) {
        return tabIndex === this.state.activeIndex;
    };
    TabsWidget.prototype.isTabFocused = function (tabIndex) {
        return tabIndex === this.state.focusedIndex;
    };
    TabsWidget.prototype.isAnchorTabActive = function () {
        return this.isTabActive(this.props.anchorTabIndex);
    };
    TabsWidget.prototype.renderTabPanel = function (index) {
        var props = {
            ariaLabelledBy: this.buildTabId(index),
            containerStyle: this.getTabPanelContainerStyle(index),
            content: this.props.tabs[index].panelContent,
            id: this.buildTabPanelId(index)
        };
        return React.createElement(TabPanel_1.TabPanel, tslib_1.__assign({}, props));
    };
    TabsWidget.prototype.getTabPanelContainerStyle = function (index) {
        if (index !== this.props.anchorTabIndex) {
            return Styles.NonAnchorTabPanelStyles;
        }
        return this.isAnchorTabActive() ? Styles.ActiveTabVisibility : Styles.InactiveTabVisibility;
    };
    TabsWidget.prototype.isAnIconDefinedInTabs = function () {
        for (var idx = 0; idx < this.props.tabs.length; idx++) {
            if (!!this.props.tabs[idx].tabContent.icon) {
                return true;
            }
        }
        return false;
    };
    TabsWidget.prototype.render = function () {
        var isAnIconDefinedInTabs = this.isAnIconDefinedInTabs();
        var tabListContainerProps = {
            'aria-label': this.props.ariaLabel,
            'data-tb-test-id': Declarative_1.makeTestId(this.props.testId, 'tablist'),
            role: 'tablist',
            style: Styles.GetTabListStyles(isAnIconDefinedInTabs),
        };
        if (this.props.dimensionOverrides && this.props.dimensionOverrides.tabHeight) {
            tabListContainerProps.style.height = this.props.dimensionOverrides.tabHeight;
        }
        var tabPanelScrollContainerProps = {
            'data-tb-test-id': Declarative_1.makeTestId(this.props.testId, 'tabpanel-scroll-container'),
            style: tslib_1.__assign({}, Styles.TabPanelScrollContainerStyles),
        };
        if (this.props.dimensionOverrides && this.props.dimensionOverrides.tabPanelPadding) {
            tabPanelScrollContainerProps.style.padding = this.props.dimensionOverrides.tabPanelPadding;
        }
        var tabPanelContainerProps = {
            'data-tb-test-id': Declarative_1.makeTestId(this.props.testId, 'tabpanel-container'),
            style: Styles.TabPanelContainerStyles
        };
        return (React.createElement("div", null,
            React.createElement(FocusIndicator_1.__UNSTABLE__INTERNAL__FocusIndicator, null,
                React.createElement("div", tslib_1.__assign({}, tabListContainerProps), this.renderTabList())),
            React.createElement("div", tslib_1.__assign({}, tabPanelScrollContainerProps),
                React.createElement("div", tslib_1.__assign({}, tabPanelContainerProps),
                    this.renderTabPanel(this.props.anchorTabIndex),
                    Declarative_1.conditional(!this.isAnchorTabActive(), this.renderTabPanel(this.state.activeIndex))))));
    };
    return TabsWidget;
}(React.PureComponent));
exports.TabsWidget = TabsWidget;
//# sourceMappingURL=TabsWidget.js.map